'use client';

import { useState, useEffect } from 'react';
import { projectsData } from '@/data/projectsData';
import GalleryGrid from '@/components/GalleryGrid';

const ProjectPageContent = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);

  const categories = [
    { id: 'commercial', title: 'Commercial' },
    { id: 'federal-projects', title: 'Federal Projects' },
    { id: 'generators', title: 'Generators' },
    { id: 'historical-renovations', title: 'Historical Renovations' },
    { id: 'new-construction', title: 'New Construction' },
    { id: 'restaurants', title: 'Restaurants' },
    { id: 'warehouses', title: 'Warehouses' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      // Show button when scrolled down more than 300px
      setShowScrollToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const h2 = element.querySelector('h2');
      const targetElement = h2 || element;
      const isLargeScreen = window.innerWidth >= 1024;
      const headerOffset = isLargeScreen ? 150 : 450;
      const elementPosition = targetElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
      setIsDropdownOpen(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <div className="flex flex-col gap-8 bg-white p-4 py-8 md:p-12 lg:flex-row lg:p-20">
      <div className="z-40 h-fit lg:sticky lg:top-[150px] lg:w-56 lg:flex-shrink-0">
        <div className="lg:hidden">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-full rounded-lg border-2 border-brittainRed bg-white px-4 py-3 text-left text-sm font-semibold text-brittainRed uppercase transition-all duration-200 hover:bg-brittainRed hover:text-white focus:ring-2 focus:ring-brittainRed focus:ring-offset-2 focus:outline-none md:text-base"
            aria-expanded={isDropdownOpen}
            aria-label="Toggle categories menu"
          >
            {isDropdownOpen ? 'Close Categories' : 'Select Project Category'}
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="mt-2 rounded-lg border-2 border-brittainRed bg-white shadow-lg">
              <div className="flex flex-col gap-0 p-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => scrollToSection(category.id)}
                    className="w-full cursor-pointer rounded-lg px-4 py-3 text-left text-sm text-black uppercase transition-all duration-200 hover:bg-brittainRed hover:text-white focus:ring-2 focus:ring-brittainRed focus:ring-offset-2 focus:outline-none md:text-base"
                    aria-label={`Scroll to ${category.title} section`}
                  >
                    {category.title}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar - Visible on xl screens and above */}
        <div className="hidden lg:flex lg:flex-col lg:gap-3">
          <h3 className="mb-2 text-lg font-bold text-brittainRed uppercase lg:text-xl">
            Categories
          </h3>
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => scrollToSection(category.id)}
              className="w-full cursor-pointer rounded-lg bg-white text-left text-sm text-black uppercase transition-all duration-200 hover:text-brittainRed focus:ring-2 focus:ring-brittainRed focus:ring-offset-2 focus:outline-none md:text-base"
              aria-label={`Scroll to ${category.title} section`}
            >
              {category.title}
            </button>
          ))}
        </div>
      </div>

      {/* Gallery Content */}
      <div className="flex flex-1 flex-col gap-12 lg:gap-20">
        <GalleryGrid
          sectionTitle="Commercial"
          images={projectsData?.commercial}
          sectionId="commercial"
        />

        <GalleryGrid
          sectionTitle="Federal Projects"
          images={projectsData?.federalProjects}
          sectionId="federal-projects"
        />

        <GalleryGrid
          sectionTitle="Generators"
          images={projectsData?.generators}
          sectionId="generators"
        />

        <GalleryGrid
          sectionTitle="Historical Renovations"
          images={projectsData?.historicalRenovations}
          sectionId="historical-renovations"
        />

        <GalleryGrid
          sectionTitle="New Construction"
          images={projectsData?.newConstruction}
          sectionId="new-construction"
        />

        <GalleryGrid
          sectionTitle="Restaurants"
          images={projectsData?.restaurants}
          sectionId="restaurants"
        />

        <GalleryGrid
          sectionTitle="Warehouses"
          images={projectsData?.warehouses}
          sectionId="warehouses"
        />
      </div>

      {/* Scroll To Top Button - Visible on mobile through medium screens */}
      {showScrollToTop && (
        <button
          onClick={scrollToTop}
          className="fixed right-6 bottom-6 z-50 flex size-12 items-center justify-center rounded-full bg-brittainRed text-white shadow-lg transition-all duration-200 hover:scale-110 hover:bg-brittainRed/90 focus:ring-2 focus:ring-brittainRed focus:ring-offset-2 focus:outline-none lg:hidden"
          aria-label="Scroll to top"
          title="Scroll to top"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2.5}
            stroke="currentColor"
            className="size-6"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default ProjectPageContent;
